/**
 *  Shows drop dates and times in the visitor's own timezone.
 *
 *  Liquid renders them in UK time, e.g. 'Fri 30 Oct · 7 pm UK'. For visitors elsewhere, this
 *  rewrites every [data-drop-local-time] element (which needs a data-timestamp, in seconds):
 *  - [data-drop-date] children get the date, put into their data-template's '{date}' so any
 *    translated wording around it is kept. data-date-style="short" gives 'Fri 30 Oct',
 *    otherwise 'Friday 30th October'.
 *  - A [data-drop-time] child gets the time and timezone, e.g. '3 pm EDT'.
 *  UK visitors on the English store keep the server text so it doesn't change after loading.
 *  UK visitors in other languages get the date in their language, as Liquid's day and month
 *  names are always English, and keep the element's translated data-timezone-label, e.g. 'UK'.
 */
(() => {
	if (window.DropTime) return;

	const isGmtOffset = (name) => name.startsWith('GMT') || name.startsWith('UTC');

	const DropTime = {
		visitorTimeZone() {
			try {
				return Intl.DateTimeFormat().resolvedOptions().timeZone;
			} catch (error) {
				return null;
			}
		},

		isEnglish(locale) {
			return new Intl.DateTimeFormat(locale).resolvedOptions().locale.startsWith('en');
		},

		/**
		 * Whether an English store visitor's browser is set to US English, so they get US date order
		 */
		isUsVisitor() {
			const browserLanguage = (navigator.languages && navigator.languages[0]) || navigator.language || '';
			return browserLanguage.toLowerCase() === 'en-us';
		},

		/**
		 * English matches the Liquid versions ('Fri 30 Oct', 'Friday 30th October'), apart from US
		 * visitors, who get 'Fri, Oct 30' and 'Friday, October 30'. Other languages use their own
		 * format, e.g. 'Fr., 30. Okt.' or 'Freitag, 30. Oktober'
		 */
		formatDate(date, locale, style) {
			const long = style !== 'short';
			const options = { weekday: long ? 'long' : 'short', day: 'numeric', month: long ? 'long' : 'short' };
			if (!this.isEnglish(locale)) return new Intl.DateTimeFormat(locale, options).format(date);
			if (this.isUsVisitor()) return new Intl.DateTimeFormat('en-US', options).format(date);
			if (!long) return new Intl.DateTimeFormat('en-GB', options).format(date);

			const parts = new Intl.DateTimeFormat('en-GB', options).formatToParts(date);
			const part = (type) => parts.find((p) => p.type === type).value;
			const suffixes = { one: 'st', two: 'nd', few: 'rd', other: 'th' };
			const day = parseInt(part('day'), 10);
			const suffix = suffixes[new Intl.PluralRules('en-GB', { type: 'ordinal' }).select(day)];
			return `${part('weekday')} ${day}${suffix} ${part('month')}`;
		},

		/**
		 * '3 pm EDT', or '11:30 pm GMT+5:30' where there are minutes or no short timezone name.
		 * Drops ':00' and lowercases am/pm to match the Liquid versions' '7 pm UK'.
		 * zoneLabel replaces the timezone name, e.g. to keep 'UK' for UK visitors.
		 */
		formatTime(date, locale, zoneLabel) {
			const parts = new Intl.DateTimeFormat(locale, {
				hour: 'numeric',
				minute: '2-digit',
				timeZoneName: 'short',
			}).formatToParts(date);
			const onTheHour = parts.find((p) => p.type === 'minute')?.value === '00';
			const hour12 = parts.some((p) => p.type === 'dayPeriod');
			const zone = parts.find((p) => p.type === 'timeZoneName');
			if (zone) zone.value = zoneLabel || this.timeZoneName(date, locale, zone.value);

			return parts
				.filter((p, i) => {
					// Only 12-hour times drop ':00', '20:00' stays as it is
					if (!(onTheHour && hour12)) return true;
					if (p.type === 'minute') return false;
					return !(p.type === 'literal' && parts[i + 1]?.type === 'minute');
				})
				.map((p) => (p.type === 'dayPeriod' ? p.value.toLowerCase() : p.value))
				.join('')
				.replace(/\s+/g, ' ')
				.trim();
		},

		/**
		 * Each English variant only abbreviates its own region's timezones (en-GB has 'CET' but
		 * not 'EDT', en-US the opposite), so for English try them in turn before settling for a
		 * GMT offset like 'GMT+9'
		 */
		timeZoneName(date, locale, fallback) {
			if (!isGmtOffset(fallback) || !this.isEnglish(locale)) return fallback;

			for (const englishLocale of ['en-GB', 'en-US', 'en-AU', 'en-IN', 'en-NZ', 'en-CA']) {
				const name = new Intl.DateTimeFormat(englishLocale, { timeZoneName: 'short' })
					.formatToParts(date)
					.find((p) => p.type === 'timeZoneName')?.value;
				if (name && !isGmtOffset(name)) return name;
			}
			return fallback;
		},

		localise(root = document) {
			const elements = root.querySelectorAll('[data-drop-local-time]');

			try {
				const timeZone = this.visitorTimeZone();
				if (!timeZone) return;
				const locale = document.documentElement.lang || undefined;
				const inUk = timeZone === 'Europe/London';
				// The server text is already right for UK visitors on the English store
				if (inUk && this.isEnglish(locale)) return;

				elements.forEach((element) => this.localiseElement(element, locale, inUk));
			} catch (error) {
				// Any element that couldn't be formatted keeps the server's UK text
				console.error('drop-time.js could not localise the drop time', error);
			} finally {
				// Lets the CSS that hides these until they're localised show them straight away, even
				// if localising failed, so they're never left waiting for the CSS fallback
				elements.forEach((element) => element.setAttribute('data-drop-localised', ''));
			}
		},

		localiseElement(element, locale, inUk) {
			const dropTime = new Date(parseInt(element.dataset.timestamp, 10) * 1000);
			if (Number.isNaN(dropTime.getTime())) return;

			const date = this.formatDate(dropTime, locale, element.dataset.dateStyle);
			const zoneLabel = inUk ? element.dataset.timezoneLabel : undefined;
			const time = this.formatTime(dropTime, locale, zoneLabel);

			// Formatted before anything is changed, so an error can't leave it half-rewritten
			element.querySelectorAll('[data-drop-date]').forEach((dateElement) => {
				dateElement.textContent = (dateElement.dataset.template || '{date}').replace('{date}', date);
			});
			const timeElement = element.querySelector('[data-drop-time]');
			if (timeElement) timeElement.textContent = time;
		},
	};

	window.DropTime = DropTime;
	DropTime.localise();
	// Sections re-rendered in the theme editor
	document.addEventListener('shopify:section:load', (event) => DropTime.localise(event.target));
})();
